import type React from "react";
import { useMemo } from "react";
import { TableCell, TableRow, Typography } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useTranslation } from "react-i18next";
import DetailSectionCard from "../common/DetailSectionCard";
import Table from "../common/Table";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { DashboardTeamRow } from "../../types/dashboard";
import { formatRupiah } from "../../utils/formatRupiah";

type TranslationKeys = keyof typeof translations.en;

type DashboardTeamTableRow = DashboardTeamRow & { _id: string };

interface DashboardTeamTableProps {
  teams: DashboardTeamRow[];
  selectedTeamOperationsId: string;
  onTeamSelect: (teamOperationsId: string) => void;
}

const columns = [
  { id: "name", label: "Name" },
  { id: "outstandingBalance", label: "Outstanding Saldo" },
  { id: "ordersInPeriod", label: "dashboard.ordersInPeriod" },
  { id: "totalPaidInPeriod", label: "dashboard.paidInPeriod" },
];

const DashboardTeamTable: React.FC<DashboardTeamTableProps> = ({
  teams,
  selectedTeamOperationsId,
  onTeamSelect,
}) => {
  const { t } = useTranslation();

  const sortedTeams = useMemo<DashboardTeamTableRow[]>(
    () =>
      [...teams]
        .sort((a, b) => {
          const outstandingDiff = Math.abs(b.outstandingBalance) - Math.abs(a.outstandingBalance);
          if (outstandingDiff !== 0) {
            return outstandingDiff;
          }
          return a.name.localeCompare(b.name);
        })
        .map((team) => ({ ...team, _id: team.teamOperationsId })),
    [teams],
  );

  return (
    <DetailSectionCard
      title={t("Teams" as TranslationKeys)}
      icon={<GroupsOutlinedIcon fontSize="small" />}
      iconColor="#0EA5E9"
      data-testid={TestId.DASHBOARD.TEAM_TABLE}
    >
      {sortedTeams.length === 0 ? (
        <Typography color="text.secondary">{t("dashboard.noTeams" as TranslationKeys)}</Typography>
      ) : (
        <Table<DashboardTeamTableRow>
          data={sortedTeams}
          columns={columns.map((column) => ({
            id: column.id,
            label: t(column.label as TranslationKeys),
          }))}
          showSearch={false}
          renderRow={(team) => (
            <TableRow
              key={team.teamOperationsId}
              hover
              onClick={() =>
                onTeamSelect(
                  selectedTeamOperationsId === team.teamOperationsId ? "" : team.teamOperationsId,
                )
              }
              sx={{ cursor: "pointer" }}
              data-testid={TestId.DASHBOARD.TEAM_ROW(team.teamOperationsId)}
            >
              <TableCell>{team.name}</TableCell>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupiah(team.outstandingBalance)}
              </TableCell>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                {team.ordersInPeriod}
              </TableCell>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupiah(team.totalPaidInPeriod)}
              </TableCell>
            </TableRow>
          )}
        />
      )}
    </DetailSectionCard>
  );
};

export default DashboardTeamTable;
